import Vue from "vue";
import axios from "axios";
import moment from "moment";
import { get, merge } from "lodash";

/**
 * The Store Pattern
 * actions -> mutations
 * Never mutate the store directly.
 */

// initial state
const state = {
  reservationId: 0,
  currentTime: "",
  currentTimeTimeoutId: 0,
  fulfillmentType: "",
  expiration: {},
  location: {},
  store: {},
  date: "",
  expiry: "",
  id: 0,
  allowAlcohol: "",
  startTime: "",
  endTime: "",
  timeslotId: "",
  context: {},
  isUpdatingTimeslot: false,
};

const getters = {
  reservationTimeslotModalOpen: state => {
    return state.context.modalId === "change-timeslot-modal";
  },
  reservationId: state => state.reservationId,
  reservationTimeslotId: state => state.timeslotId,
  reservationActive: state => state.expiration.isActive,
  reservationSelectedDate: state => state.date,
  reservationTimeslotInfo: ({ startTime, endTime, date }) => ({
    startTime,
    endTime,
    date
  }),
  reservationFulfillmentType: state => state.fulfillmentType,
  reservationExpiry: state => state.expiry,
  reservationUniqueTimeslotId: state => {
    const dateExists = !!state.date;
    const timeslotSelected = !!state.timeslotId;
    if (dateExists && timeslotSelected) {
      return `${state.date}${state.timeslotId}`;
    }
    return null;
  },
  updatingTimeslot: state => state.isUpdatingTimeslot,
  // COVID: Remove once healthcare program is done
  storeSupportsMedTimeslot: (state) => {
    return get(state.store, "supportsMedTimeslot", false);
  },
  storeId: state => get(state.store, 'id')
};

// actions - can be async
const actions = {
  /**
   * post a new timeslot for this reservation
   * @param {string} date - date of the new timeslot
   * @param {object} timeslot - timeslot object with a single property "id" (number)
   * @param {boolean} ignoreCartChangeWarnings - whether to ignore the cart change warnings
   * @return {promise} promise returns the response status (number)
   */
  POST_NEW_TIMESLOT({ COMMIT }, { date, timeslot, ignoreCartChangeWarnings }) {
    // console.log('POST_NEW_TIMESLOT()', date, timeslot, ignoreCartChangeWarnings)
    var vm = this;
    vm.commit("errors/CLEAR_ERRORS");
    vm.commit('reservation/SET_UPDATING_TIMESLOT', true);

    const data = {
      ignoreCartChangeWarnings,
      date,
      timeslot
    };
    // console.log('@ data:', data)

    return axios
      .all([axios.post("/commerce-api/v1/timeslot/reservations", data)])
      .then(
        axios.spread(response => {
          // console.log('~ POST_NEW_TIMESLOT()', response.data)
          const { status, data } = response;

          vm.commit("reservation/UPDATE_TIMESLOT", data);
          vm.commit("reservation/UPDATE_EXPIRATION", data.currentTime);

          // reload the whole cart
          vm.dispatch("cart/LOAD_CART");

          return status;
        })
      )
      .catch(error => {
        const status = get(error, "response.status");

        if (status === 409) {
          error.type = "cartChange";
          error.variant = "warning";
        } else {
          error.type = "timeslotPicker";
        }
        vm.dispatch("errors/HANDLE_ERROR", error);

        return status;
      })
      .finally(() => {
        vm.commit('reservation/SET_UPDATING_TIMESLOT', false);
      });
  }
};

// mutations - Must be synchronous
const mutations = {
  /**
   * create the reservation (typically from /cart/full)
   * @param {object} data - data to create the reservation in store
   */
  CREATE_RESERVATION(state, data) {
    // console.log('CREATE_RESERVATION()', data, state)

    // because data from cart requests aren't the same as data from timeslot requests
    const {
      contactInfo = {},
      timeslot = {},
      currentTime // required
    } = data;

    // because we're not getting store back in POST /timeslot/reservations
    // we sometimes need to use the store that we already have in $store
    // todo: shouldn't need this one anymore, because this mutation is only called from LOAD_CART

    const store = data.store || state.store;

    // fulfillment type is required, so get it any way we can
    const fulfillmentType =
      timeslot.fulfillmentType || data.curbsideFulfillmentMode;

    // set location
    const location = fulfillmentType === "delivery" ? contactInfo : store;

    // stores have a name, and delivery addresses (usually) have a nickname
    if (!location.nickname) {
      location.nickname = location.name || location.address1 || "";
    }

    if (!location.name) {
      location.name = location.nickname || "";
    }

    Vue.set(state, "currentTime", currentTime);
    Vue.set(state, "store", store);
    Vue.set(state, "fulfillmentType", fulfillmentType);
    Vue.set(state, "location", location);

    const { reservationId = "", expiry } = timeslot;
    const isPastExpiry = moment(expiry).isBefore(moment());

    if (!!reservationId && !isPastExpiry) {
      //this reservation is still active save it in store
      Vue.set(state, "reservationId", reservationId);
    }
    //otherwise the reservation is expired and we don't want to track it
  },

  /**
   * update reservation timeslot
   * @param {object} data - data to update the timeslot
   */
  UPDATE_TIMESLOT(state, data) {
    // console.log('UPDATE_TIMESLOT()', data)

    const { timeslot = {} } = data;
    const expiry = data.expiry || timeslot.expiry; // todo: get these consistently from api?
    const isPastExpiry = moment(expiry).isBefore(moment());

    if (!isPastExpiry) {
      Vue.set(state, "expiry", expiry);
      Vue.set(state, "id", data.id);

      const allowAlcohol =
        timeslot.allow_alcohol || timeslot.allowAlcohol || "";
      const timeslotId = timeslot.id || "";

      const date =
        timeslot.date ||
        data.date ||
        moment.parseZone(timeslot.startTime).format("YYYY-MM-DD");

      Vue.set(state, "allowAlcohol", allowAlcohol);
      Vue.set(state, "date", date);
      Vue.set(state, "startTime", timeslot.startTime);
      Vue.set(state, "endTime", timeslot.endTime);
      Vue.set(state, "timeslotId", timeslotId);
    }
  },

  /**
   * update the reservation fulfillment type
   * @param {string} type
   */
  UPDATE_FULFILLMENT_TYPE(state, type) {
    // console.log('UPDATE_FULFILLMENT_TYPE()', type)
    Vue.set(state, "fulfillmentType", type);
  },

  /**
   * update the reservation location
   * @param {object} location - location info, including nickname & postalCode
   */
  UPDATE_LOCATION(state, location) {
    Vue.set(state, "location", location);
  },

  /**
   * update the reservation store
   * @param {object} store - store info, including id & postalCode
   */
  UPDATE_STORE(state, store) {
    // console.log('UPDATE_STORE()', store)
    Vue.set(state, "store", store);
  },

  /**
   * update the current time & expiration info
   * @param {string} currentTime - current time
   */
  UPDATE_EXPIRATION(state, currentTime) {
    // console.log('UPDATE_EXPIRATION()', currentTime, state)

    // get the current time
    if (currentTime) {
      Vue.set(state, "currentTime", currentTime);
    } else {
      currentTime = state.currentTime;
    }

    // get expiry
    const { expiry } = state;

    // const expiry = moment(currentTime).subtract(5, 'minutes')

    // console.log('@ currentTime:', currentTime)
    // console.log('@ expiry:', expiry)

    // init with defaults
    const init = {
      in: {},
      at: expiry,
      isActive: false,
      isPassed: false
    };

    // set up the "expiration" property
    const cur = moment(currentTime); // if undefined, uses "now"
    const exp = moment(expiry); // if undefined, uses "now"

    // check `expiry` for "undefined" (not `exp`)
    const expiration =
      currentTime && expiry
        ? {
            in: {
              hours: exp.diff(cur, "hours"),
              minutes: exp.diff(cur, "minutes"),
              seconds: exp.diff(cur, "seconds")
            },
            isActive: exp.isAfter(cur),
            isPassed: !exp.isAfter(cur)
          }
        : {};

    Vue.set(state, "expiration", merge({}, init, expiration));

    // when we have an active timeslot chosen
    // update the current time & expiration at a set interval
    if (expiration.isActive) {
      const INTERVAL_SECONDS = 10;

      // clear the previous timeout (so we don't update more than once during the interval)
      window.clearTimeout(state.currentTimeTimeoutId);

      const newTime = moment(currentTime)
        .add(INTERVAL_SECONDS, "seconds") // add time
        .format("YYYY-MM-DDTHH:mm:ssZZ"); // this should match the "currentTime" returned from api, to be consistent

      const timeoutId = window.setTimeout(() => {
        // recursively run this update with the new time
        this.commit("reservation/UPDATE_EXPIRATION", newTime);
      }, INTERVAL_SECONDS * 1000);

      // commit the timeoutId (so if something else triggers this function, we can clear it)
      Vue.set(state, "currentTimeTimeoutId", timeoutId);
    }
  },

  /** clear the reservation timeslot */
  CLEAR_TIMESLOT(state) {
    // console.log('CLEAR_TIMESLOT()', state)
    this.commit("reservation/UPDATE_TIMESLOT", {});
  },

  /** set the context, for moving thru different user flows */
  SET_CONTEXT(state, context) {
    // console.log('SET_MODAL_CONTEXT()', context)
    Vue.set(state, "context", context);
  },

  /**
   * set the updating timeslot state
   * @param {boolean} updating
   */
  SET_UPDATING_TIMESLOT(state, updating) {
    Vue.set(state, 'isUpdatingTimeslot', updating);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
